import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Button,
  IconButton,
  Chip,
  Divider,
  Skeleton,
  TextField,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { NumberFormatter } from "../components/NumberFormerter";
import BackHeader from "../components/BackHeader";
import { useNavigate } from "react-router-dom";
import { WalletService } from "../services/wallet/wallet";
import { Colors } from "../utils/colors";
import { ProfileKYCService } from "../services/profilekyc/profilekyc";
import { useSelector , useDispatch} from "react-redux";
import {
  setIsAuthenticated,
  setUser,
  setSessionExpired
} from "../Redux/Reducers/appState";
import usePendingPaymentRecovery from "../hooks/usePendingPaymentRecovery";

export default function WalletPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const [balance, setBalance] = useState(0);
  const [withdrawableBalance, setWithdrawableBalance] = useState(0);
  const [lockedWinningsBalance, setLockedWinningsBalance] = useState(0);
  const [lockedWinningsUnlockAt, setLockedWinningsUnlockAt] = useState("");
  const [lockedWinningsDrawCode, setLockedWinningsDrawCode] = useState("");
  const [history, setHistory] = useState([]);
  const [bankAccount, setBankAccount] = useState(null);
  const [cashOutAmount, setCashOutAmount] = useState("");
  const [cashOutLoading, setCashOutLoading] = useState(false);
  const [cashOutMessage, setCashOutMessage] = useState({
    type: "info",
    text: ""
  });
  const [loading, setLoading] = useState(true);
  const accessToken = useSelector((state) => state.appState.acesssToken);
  const sessionExpired = useSelector((state) => state.appState.sessionExpired);

  const fetchWallet = async () => {
    try {
      const response = await WalletService.getHistory();
      if(response?.message === "Invalid or expired token") {
         dispatch(setUser(null));
         dispatch(setSessionExpired(true))
      }
      setHistory(response || []);

      const res = await WalletService.getBalance();
      if(res?.message === "Invalid or expired token") {
         dispatch(setUser(null));
         dispatch(setSessionExpired(true))
      }
      setBalance(res.balance || 0);
      setWithdrawableBalance(res.withdrawable_balance || 0);
      setLockedWinningsBalance(res.pending_winnings_balance || res.locked_winnings_balance || 0);
      setLockedWinningsUnlockAt(res.pending_winnings_available_at || res.locked_winnings_unlock_at || "");
      setLockedWinningsDrawCode(res.locked_winnings_draw_code || "");
    } catch (err) {
      console.log("Wallet err:", err);
    }
  };

  const fetchBankAccount = async () => {
    try {
      const response = await ProfileKYCService.getProfileKYC(accessToken);
      const accounts = response?.bankAccounts || [];
      const primaryBank = accounts.find((item) => Number(item.is_primary) === 1) || accounts[0] || null;
      setBankAccount(primaryBank);
    } catch (error) {
      console.log("Bank account err:", error);
    }
  };

  const handleCashOut = async () => {
    const amount = Number(cashOutAmount || 0);

    if (!bankAccount) {
      setCashOutMessage({
        type: "error",
        text: "No registered bank account found for cash out."
      });
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      setCashOutMessage({
        type: "error",
        text: "Enter a valid cash out amount."
      });
      return;
    }

    if (amount > Number(withdrawableBalance || 0)) {
      setCashOutMessage({
        type: "error",
        text: "Cash out amount cannot be more than your currently available balance."
      });
      return;
    }

    setCashOutLoading(true);

    try {
      const response = await WalletService.cashOut(amount);

      if (!response?.status) {
        setCashOutMessage({
          type: "error",
          text: response?.message || "Cash out failed."
        });
        setCashOutLoading(false);
        return;
      }

      setCashOutMessage({
        type: "success",
        text: response?.message || "Cash out initiated successfully."
      });
      setCashOutAmount("");
      await fetchWallet();
    } catch (error) {
      console.log("Cash out error:", error);
      setCashOutMessage({
        type: "error",
        text: error?.message || "Unable to process cash out right now."
      });
    }

    setCashOutLoading(false);
  };

  const { paymentAlert } = usePendingPaymentRecovery({
    expectedType: "wallet_funding",
    onSuccess: async () => {
      await fetchWallet();
    }
  });

   useEffect(() => {
            if (sessionExpired) {
              dispatch(setIsAuthenticated(false));
              setTimeout(() => {
                navigate("/login");
              }, 500);
              return;
            }
          }, [sessionExpired]);

  
  useEffect(() => {
    const loadWallet = async () => {
      await Promise.all([fetchWallet(), fetchBankAccount()]);
      setLoading(false);
    };

    loadWallet();

    const interval = setInterval(() => {
      fetchWallet();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        p: 3,
        minHeight: "100vh",
        background: "linear-gradient(180deg,#001f3f,#003366,#0b2a4a)",
        color: "white",
      }}
    >
      <BackHeader title="Wallet" />

      {paymentAlert ? (
        <Alert severity={paymentAlert.severity} sx={{ mb: 2 }}>
          {paymentAlert.text}
        </Alert>
      ) : null}

      
      <Card
        sx={{
          p: 3,
          borderRadius: 2,
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.08)",
          backdropFilter: "blur(6px)",
          mb: 3,
        }}
      >
        
        <Typography sx={{ opacity: 0.8 , color:Colors.whitesmoke }}>Wallet Balance</Typography>

        <Stack direction="row" alignItems="center" spacing={1} mt={1}>
          <Typography variant="h4" fontWeight={900} sx={{ color:Colors.whitesmoke }} >
            {visible ? `₦${NumberFormatter(balance)}` : "******"}
          </Typography>

          <IconButton
            sx={{ color: "#ffb300" }}
            onClick={() => setVisible(!visible)}
          >
            {visible ? <VisibilityOffIcon /> : <RemoveRedEyeIcon />}
          </IconButton>
        </Stack>

        <Typography sx={{ mt: 1.5, color: "rgba(255,255,255,0.72)" }}>
          Available for Cash Out: ₦{NumberFormatter(withdrawableBalance)}
        </Typography>

        {Number(lockedWinningsBalance || 0) > 0 ? (
          <Typography sx={{ mt: 0.75, color: "rgba(255,255,255,0.6)" }}>
            Pending Winnings: ₦{NumberFormatter(lockedWinningsBalance)}
          </Typography>
        ) : null}

        
        <Button
          variant="contained"
          fullWidth
          sx={{
            mt: 3,
            py: 1.4,
            bgcolor: "#5fd068",
            color: "#003300",
            fontWeight: 700,
            borderRadius: 1,
            "&:hover": { bgcolor: "#4ac45a" },
          }}
          onClick={() => navigate("/account/authmenu/account/fund-wallet")}
        >
          Fund Wallet
        </Button>

        <Button
          variant="outlined"
          fullWidth
          sx={{
            mt: 1.5,
            py: 1.2,
            borderColor: "rgba(255,255,255,0.25)",
            color: "rgba(255,255,255,0.7)"
          }}
          onClick={() => navigate("/buy")}
        >
          Buy Ticket From Wallet
        </Button>
      </Card>

      <Card
        sx={{
          p: 3,
          borderRadius: 2,
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.08)",
          backdropFilter: "blur(6px)",
          mb: 3,
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ color: "#fff", mb: 1 }}>
          Cash Out to Registered Bank Account
        </Typography>

        <Typography sx={{ color: "rgba(255,255,255,0.72)", mb: 2 }}>
          Cash out from your wallet directly to the bank account registered on your profile.
        </Typography>

        <Typography sx={{ color: "rgba(255,255,255,0.72)", mb: 2 }}>
          Minimum cash-out amount is ₦100. Available now: ₦{NumberFormatter(withdrawableBalance)}
        </Typography>

        {Number(lockedWinningsBalance || 0) > 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {`₦${NumberFormatter(lockedWinningsBalance)} in draw winnings is pending and is not included in your wallet balance${
              lockedWinningsDrawCode ? ` (${lockedWinningsDrawCode})` : ""
            }${
              lockedWinningsUnlockAt
                ? `, available after: ${new Date(lockedWinningsUnlockAt).toLocaleString()}.`
                : "."
            }`}
          </Alert>
        ) : null}

        {cashOutMessage.text ? (
          <Alert severity={cashOutMessage.type} sx={{ mb: 2 }}>
            {cashOutMessage.text}
          </Alert>
        ) : null}

        <Card
          sx={{
            p: 2,
            mb: 2,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)"
          }}
        >
          <Typography sx={{ color: "#ffb300", fontWeight: 700, mb: 1 }}>
            Registered Bank Account
          </Typography>

          {bankAccount ? (
            <Stack spacing={0.75}>
              <Typography sx={{ color: "#fff" }}>
                <strong>Bank:</strong> {bankAccount.bank_name}
              </Typography>
              <Typography sx={{ color: "#fff" }}>
                <strong>Account Number:</strong> {bankAccount.account_number}
              </Typography>
              <Typography sx={{ color: "#fff" }}>
                <strong>Account Name:</strong> {bankAccount.account_name}
              </Typography>
            </Stack>
          ) : (
            <Typography sx={{ color: "rgba(255,255,255,0.72)" }}>
              No registered bank account found.
            </Typography>
          )}
        </Card>

        <TextField
          label="Cash Out Amount"
          type="number"
          fullWidth
          value={cashOutAmount}
          onChange={(e) => setCashOutAmount(e.target.value)}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-input": { color: "#fff" },
            "& .MuiInputLabel-root": { color: "#fff" },
            "& .MuiOutlinedInput-root fieldset": { borderColor: "rgba(255,255,255,0.25)" }
          }}
          InputLabelProps={{ shrink: true }}
        />

        <Button
          variant="contained"
          fullWidth
          onClick={handleCashOut}
          disabled={cashOutLoading || !bankAccount}
          sx={{
            py: 1.4,
            bgcolor: "#ffb300",
            color: "#1a1a1a",
            fontWeight: 700,
            borderRadius: 1,
            "&:hover": { bgcolor: "#f0aa00" }
          }}
        >
          {cashOutLoading ? "Processing..." : "Cash Out"}
        </Button>
      </Card>

      
      <Card
        sx={{
          p: 3,
          borderRadius: 2,
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.08)",
          backdropFilter: "blur(6px)",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ color: "white" }}>
            Transaction History
          </Typography>

          <Chip
            label="Latest"
            sx={{
              bgcolor: "rgba(255,255,255,0.15)",
              color: "#ffb300",
              fontWeight: 600,
            }}
          />
        </Stack>

        <Divider sx={{ mb: 2, borderColor: "rgba(255,255,255,0.1)" }} />

        
        {loading ? (
          [...Array(5)].map((_, i) => (
            <Skeleton
              key={i}
              height={40}
              sx={{ bgcolor: "rgba(255,255,255,0.1)", mb: 2 }}
            />
          ))
        ) : history.length === 0 ? (
          <Typography sx={{ opacity: 0.7, color: "white", textAlign: "center" }}>
            No transactions yet.
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                <TableCell sx={th}>Type</TableCell>
                  <TableCell sx={th}>Reference / Ticket</TableCell>
                  <TableCell sx={th}>Details</TableCell>
                  <TableCell sx={th}>Status</TableCell>
                  <TableCell sx={th}>Amount</TableCell>
                  <TableCell sx={th}>Date</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {history.map((tx) => {
                  const isCredit = tx.type === "credit";
                  const isPayoutEntry =
                    tx.entry_kind === "payout" ||
                    tx.source === "payout" ||
                    tx.source === "payout_failed" ||
                    tx.source === "payout_reversal";
                  const payoutStatus =
                    tx.status === "completed"
                      ? "Successful"
                      : tx.status === "failed"
                        ? "Failed"
                        : tx.status === "processing" || tx.status === "pending"
                          ? "Pending"
                          : "--";
                  const detailText = isPayoutEntry
                    ? [tx.bank_name, tx.account_number].filter(Boolean).join(" • ") || tx.failure_reason || "--"
                    : tx.source === "draw_winning"
                      ? `Prize credit after ${Number(tx.deduction_percent || 0)}% withholding tax`
                      : tx.source === "wallet_funding"
                        ? "Wallet funded successfully"
                        : tx.source === "payout_reversal"
                          ? tx.failure_reason || "Failed payout refunded to wallet"
                          : "--";

                  return (
                    <TableRow key={tx.id}>
                      <TableCell sx={td}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {isCredit ? (
                            <ArrowDownwardIcon sx={{ color: "#5fd068" }} />
                          ) : (
                            <ArrowUpwardIcon sx={{ color: "#ef5350" }} />
                          )}
                          {tx.type?.toUpperCase()}
                        </Stack>
                      </TableCell>

                      <TableCell sx={td}>
                        {tx.ticket_number || tx.reference || "--"}
                      </TableCell>

                      <TableCell sx={td}>
                        {isPayoutEntry
                          ? detailText
                          : tx.source === "draw_winning"
                            ? `₦${Number(tx.gross_amount || tx.amount || 0).toLocaleString()}`
                            : detailText}
                      </TableCell>

                      <TableCell sx={td}>
                        {isPayoutEntry
                          ? payoutStatus
                          : tx.source === "draw_winning"
                            ? `₦${Number(tx.deduction_amount || 0).toLocaleString()} (${Number(tx.deduction_percent || 0)}%)`
                            : "--"}
                      </TableCell>

                      <TableCell sx={td}>
                        {isCredit ? "+" : "-"}₦
                        {Number(tx.amount || 0).toLocaleString()}
                      </TableCell>


                      <TableCell sx={td}>
                        {new Date(tx.created_at)?.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

    </Box>
  );
}


const th = {
  color: "white",
  fontWeight: 700,
  borderBottom: "1px solid rgba(255,255,255,0.1)",
};

const td = {
  color: "white",
  borderBottom: "1px solid rgba(255,255,255,0.05)",
};
